import { useState } from 'react';
import FileParser from '../services/FileParser';
import APIClient from '../services/APIClient';
import { getConfig } from '../config';
import { sanitizeErrorMessage } from '../utils/errorSanitizer';
import { useCredentials} from '../contexts/CredentialsContext'


// Batch size for email validation
const BATCH_SIZE = 5;
/**
 * Custom hook for managing file upload state and logic
 * @returns {Object} Upload state and functions
 */
const useFileUpload = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [parsedEmails, setParsedEmails] = useState(null);
  const [fileMetadata, setFileMetadata] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [validationResults, setValidationResults] = useState(null);
  const [validationSummary, setValidationSummary] = useState(null);
  const [validationProgress, setValidationProgress] = useState(null);

  const config = getConfig();
  const { apiKey } = useCredentials();
  const fileParser = new FileParser();
  const apiClient = new APIClient({
    baseUrl: config.apiUrl,
    timeout: 30000,
    apiKey: apiKey,
    mode: config.mode
  });

  /**
   * Parse a file and extract emails (step 1)
   * @param {File} file - The file to parse
   * @returns {Promise<void>}
   */
  const parseFile = async (file) => {
    // Reset previous state
    setIsLoading(true);
    setError(null);
    setResult(null);
    setParsedEmails(null);

    try {
      // Validate file type
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!config.allowedTypes.includes(fileExtension)) {
        throw new Error(
          `Invalid file type. Please upload ${config.allowedTypes.join(' or ')} files.`
        );
      }

      // Validate file size
      if (file.size > config.maxFileSize) {
        const maxSizeMB = (config.maxFileSize / (1024 * 1024)).toFixed(0);
        throw new Error(
          `File size exceeds the maximum limit of ${maxSizeMB}MB.`
        );
      }

      // Prepare file data for backend parsing
      const fileData = await fileParser.prepareFileForBackend(file);

      // Send to backend for parsing (instead of parsing client-side)
      const emailData = await apiClient.parseEmailFile(fileData);

      // Check if any emails were found
      if (emailData.totalRows === 0) {
        throw new Error('No valid email addresses found in the file.');
      }

      // Store parsed email data and metadata for later upload
      setParsedEmails(emailData);
      setFileMetadata({
        fileName: file.name,
        fileSize: file.size
      });
    } catch (err) {
      // Set error message (sanitized to remove sensitive information)
      setError(sanitizeErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Extract all emails from parsed data structure
   * @param {Object} emailData - Parsed email data
   * @returns {string[]} Array of all email addresses
   */
  const extractAllEmails = (emailData) => {
    if (emailData.type === 'csv') {
      // Extract all emails from CSV rows
      const allEmails = [];
      emailData.rows.forEach(row => {
        Object.values(row).forEach(email => {
          if (email) allEmails.push(email);
        });
      });
      return allEmails;
    } else {
      // Return email list for text format
      return emailData.emails;
    }
  };

  /**
   * Helper function to chunk an array into smaller batches
   * @param {Array} array - Array to chunk
   * @param {number} size - Size of each chunk
   * @returns {Array<Array>} Array of chunks
   */
  const chunkArray = (array, size) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  /**
   * Validate email rows (step 2) - with batch processing
   * @returns {Promise<void>}
   */
  const validateEmails = async () => {
    if (!parsedEmails || parsedEmails.totalRows === 0) {
      setError('No emails to validate');
      return;
    }

    // Only validate CSV files
    if (parsedEmails.type !== 'csv') {
      return;
    }

    setIsValidating(true);
    setError(null);
    setValidationProgress({ current: 0, total: parsedEmails.rows.length });

    try {
      // Split rows into batches
      const batches = chunkArray(parsedEmails.rows, BATCH_SIZE);

      let allResults = [];
      let aggregatedSummary = {
        totalEmails: 0,
        newEmails: 0,
        existingEmails: 0,
        sameUserMatches: 0,
        differentUserMatches: 0
      };

      // Process each batch sequentially
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        const batchStartIndex = i * BATCH_SIZE;

        // Update progress
        setValidationProgress({
          current: Math.min((i + 1) * BATCH_SIZE, parsedEmails.rows.length),
          total: parsedEmails.rows.length
        });

        // Call API for this batch
        const response = await apiClient.validateEmailRows(
          batch,
          parsedEmails.headers,
          180000 // 3 minute timeout
        );

        // Adjust row indices to match overall dataset
        const adjustedResults = response.validationResults.map(result => ({
          ...result,
          rowIndex: result.rowIndex + batchStartIndex
        }));

        // Accumulate results
        allResults = [...allResults, ...adjustedResults];

        // Aggregate summary statistics
        aggregatedSummary.totalEmails += response.summary.totalEmails;
        aggregatedSummary.newEmails += response.summary.newEmails;
        aggregatedSummary.existingEmails += response.summary.existingEmails;
        aggregatedSummary.sameUserMatches += response.summary.sameUserMatches;
        aggregatedSummary.differentUserMatches += response.summary.differentUserMatches;

        // Update UI with accumulated results (incremental display)
        setValidationResults([...allResults]);
        setValidationSummary({ ...aggregatedSummary });
      }

      // Final update
      setValidationResults(allResults);
      setValidationSummary(aggregatedSummary);

    } catch (err) {
      console.error('Validation error:', err);
      setError(sanitizeErrorMessage(err));
    } finally {
      setIsValidating(false);
      setValidationProgress(null);
    }
  };

  /**
   * Upload selected emails (step 3)
   * Now stores emails directly to DynamoDB via store-emails endpoint
   * @param {Array<Object>} selectedRows - Array of selected row objects
   * @param {Array<number>} selectedRowIndices - Array of selected row indices
   * @returns {Promise<void>}
   */
  const uploadEmails = async (selectedRows = null, selectedRowIndices = null) => {
    if (!parsedEmails || parsedEmails.totalRows === 0) {
      setError('No emails to upload');
      return;
    }

    // Enforce validation for CSV files
    if (parsedEmails.type === 'csv' && (!validationResults || validationResults.length === 0)) {
      setError('Please validate emails before uploading');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Extract email pairs from rows (preserving row structure)
      let emailObjects;
      const rowsToProcess = selectedRows && selectedRows.length > 0 ? selectedRows : parsedEmails.rows;

      if (parsedEmails.type === 'csv') {
        // For CSV, preserve email pairs from each row
        // BUT only include rows that passed validation
        emailObjects = [];
        rowsToProcess.forEach((row, index) => {
          // Find the validation result for this row
          const rowIndex = selectedRowIndices ? selectedRowIndices[index] : index;
          const validation = validationResults?.find(v => v.rowIndex === rowIndex);

          // Only process rows that passed validation
          if (!validation || !validation.isValid) {
            console.warn(`Skipping row ${rowIndex} - validation failed or not found`);
            return;
          }

          const emails = Object.values(row).filter(email => email && email.trim());

          if (emails.length > 0) {
            // Create one record per row with email and compareEmail
            const emailRecord = {
              email: emails[0].trim()
            };

            // Add compareEmail if there's a second email in the row
            if (emails.length > 1) {
              emailRecord.compareEmail = emails[1].trim();
            }

            emailObjects.push(emailRecord);
          }
        });

        // Check if any valid emails remain after filtering
        if (emailObjects.length === 0) {
          throw new Error('No valid emails to upload. All selected rows failed validation.');
        }
      } else {
        // For text format, keep individual emails
        const emailList = extractAllEmails(parsedEmails);
        emailObjects = emailList.map(email => ({
          email: email
        }));
      }

      // Store emails directly to DynamoDB
      const storeResult = await apiClient.storeEmails(emailObjects);

      // Set success result (format for UI compatibility)
      setResult({
        processed: emailObjects.length, // Total emails attempted
        stored: storeResult.stored,
        failed: storeResult.failed,
        duplicates: 0, // Duplicates already handled in validation step
        errors: storeResult.errors,
        stored_emails: storeResult.stored_emails || []
      });
    } catch (err) {
      // Set error message (sanitized to remove sensitive information)
      setError(sanitizeErrorMessage(err));
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Store validated emails using the new store-emails endpoint
   * @param {Array<Object>} emailsToStore - Array of email objects to store
   * @returns {Promise<void>}
   */
  const storeValidatedEmails = async (emailsToStore) => {
    if (!emailsToStore || emailsToStore.length === 0) {
      setError('No emails to store');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // Call store emails API
      const storeResult = await apiClient.storeEmails(emailsToStore);

      // Set success result
      setResult({
        stored: storeResult.stored,
        failed: storeResult.failed,
        errors: storeResult.errors,
        stored_emails: storeResult.stored_emails || []
      });
    } catch (err) {
      // Set error message (sanitized to remove sensitive information)
      setError(sanitizeErrorMessage(err));
    } finally {
      setIsUploading(false);
    }
  };

  /**
   * Reset the upload state
   */
  const reset = () => {
    setIsLoading(false);
    setIsValidating(false);
    setIsUploading(false);
    setResult(null);
    setError(null);
    setParsedEmails(null);
    setFileMetadata(null);
    setUploadProgress(0);
    setValidationResults(null);
    setValidationSummary(null);
  };

  return {
    parseFile,
    validateEmails,
    uploadEmails,
    storeValidatedEmails,
    isLoading,
    isValidating,
    isUploading,
    result,
    error,
    parsedEmails,
    fileMetadata,
    uploadProgress,
    validationResults,
    validationSummary,
    validationProgress,
    reset
  };
};

export default useFileUpload;
